import { useState, useEffect } from 'react'
import { Modal } from '@/components/common'
import { directorApi } from '@/services/api/director'
import { ApiErrorResponse } from '@/services/api/client'
import type { DirectorActionItem, DirectorDecisionCreatePayload } from '@/types/director'
import { extractErrorMessage, isDirectorDecisionConflict } from '@/utils/errorHandler'
import { MessageCircleQuestion } from 'lucide-react'

interface QuickInfoModalProps {
  isOpen: boolean
  item: DirectorActionItem | null
  onClose: () => void
  onSuccess: () => void
  /** Fallakte v2 (K7): 409-Konflikt → Liste refreschen */
  onConflict?: () => void
}

/** Fallakte v2 (P5.9): Rückfrage stellen (info_requested) direkt aus der Fallakte. */
export function QuickInfoModal({ isOpen, item, onClose, onSuccess, onConflict }: QuickInfoModalProps) {
  const [note, setNote] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (isOpen && item) {
      setNote('')
      setError(null)
    }
  }, [isOpen, item])

  if (!item) return null

  const handleSubmit = async () => {
    if (!note.trim()) {
      setError('Bitte formulieren Sie eine Frage an die Lehrperson.')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      const payload: DirectorDecisionCreatePayload = {
        substitution_request_id: item.id,
        payment_type: 'private',
        decision_type: 'info_requested',
        info_request_note: note.trim(),
      }
      await directorApi.createDecision(payload)
      onSuccess()
    } catch (e: unknown) {
      let msg = extractErrorMessage(e, { loadError: 'Ein Fehler ist aufgetreten' })
      if (e instanceof ApiErrorResponse && e.statusCode === 403) {
        const serverDetail = (e.response?.data as { detail?: string } | undefined)?.detail
        if (serverDetail) msg = serverDetail
      }
      if (isDirectorDecisionConflict(e)) {
        setError('Diese Anfrage wurde bereits von einem anderen Direktor entschieden.')
        onConflict?.()
      } else {
        setError(msg)
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Rückfrage stellen" size="md">
      <div className="space-y-4">
        <div className="p-3 bg-gray-50 rounded-lg">
          <p className="text-sm font-medium text-gray-900">{item.requesting_teacher_name}</p>
          <p className="text-xs text-gray-500 mt-0.5">
            {item.lesson_count} Lektionen
          </p>
          {item.reason && <p className="text-xs text-gray-500 italic mt-1">{item.reason}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Frage an die Lehrperson *</label>
          <textarea
            value={note}
            onChange={e => setNote(e.target.value)}
            rows={4}
            autoFocus
            className="w-full rounded-md border border-gray-300 shadow-sm px-3 py-2 text-sm"
            placeholder="Was möchten Sie wissen? Die Anfrage pausiert im Gate, bis Sie entscheiden."
          />
          <p className="text-xs text-gray-500 mt-1">
            Die Anfrage bleibt im Gate — Antwortfristen ruhen, bis Sie die Rückfrage auflösen.
          </p>
        </div>

        {error && <div className="p-3 bg-red-100 text-red-700 rounded text-sm">{error}</div>}

        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
          >
            <MessageCircleQuestion className="w-4 h-4" />
            {submitting ? 'Speichern...' : 'Rückfrage senden'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
          >
            Abbrechen
          </button>
        </div>
      </div>
    </Modal>
  )
}
